/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import {
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/api/client';
import type { components } from '@/types/api';

/**
 * Notices in Vellum, and what each person chooses to be told
 * (`notifications/spec.md` §3.2, `NOT-14` to `NOT-24`).
 *
 * A notice is a pointer, not a record: the event, the agreement and the same
 * few fields an email of that event would carry (`INT-6`). The words a person
 * reads are chosen on screen from the event's name, so the server never sends
 * a sentence the screen then has to take apart.
 */

/** The rows of Settings -> Notifications, by the key the server stores. */
export const NOTICE_KINDS = [
  'approval_waiting',
  'sign_waiting',
  'approved',
  'rejected',
  'sent_for_signature',
  'signed_or_declined',
  'people_changed',
  'withdrawn',
  'expiring_soon',
  'packet_step_done',
  'packet_handed_over',
  'approval_reminder',
  'out_of_office',
  'filed_copy_problem',
  'mentioned',
] as const;

export type NoticeKind = (typeof NOTICE_KINDS)[number];

export const noticeSchema = z.object({
  id: z.string(),
  // A string, not an enum: a newer server may name an event this build has no
  // words for, and that should read as a plain notice rather than fail the bell.
  event: z.string(),
  agreement_id: z.string().nullable(),
  title: z.string(),
  reference: z.string(),
  counterparty_name: z.string(),
  actor_name: z.string().nullable(),
  /**
   * For a reminder, the day it became the reader's turn: asked to approve,
   * ready to send, or a packet's next step ready to start (`REM-4`, `REM-6`).
   * A server that predates it does not send it, and the bell then leaves it out.
   */
  since: z.string().nullable().default(null),
  /** Whose work Amida's new signer covers, where out of office named them. */
  covering_for_name: z.string().nullable().default(null),
  /** Where work waits on somebody out of office, when they are back (ISO datetime). */
  away_until: z.iso.datetime({ offset: true }).nullable().catch(null),
  /**
   * Why it waits, as a code: `short_period` when a signature already sent
   * stays because the absence is short (`OOO-D6`). Any other value, or none
   * from an older server, reads as no reason rather than failing the bell.
   */
  stays_because: z.enum(['short_period']).nullable().catch(null),
  created_at: z.string(),
  read_at: z.string().nullable(),
  can_open: z.boolean(),
});

export const noticesSchema = z.object({
  items: z.array(noticeSchema),
  total: z.number(),
  unread_count: z.number(),
  limit: z.number(),
  offset: z.number(),
  has_more: z.boolean(),
});

export type Notice = z.infer<typeof noticeSchema>;
export type Notices = z.infer<typeof noticesSchema>;

/** How many the bell asks for. The server caps a page at 50 (`NOT-19`). */
export const BELL_PAGE = 20;

/** How often the bell checks for something new: at most once a minute (`NOT-18`). */
export const BELL_CHECK_EVERY_MS = 60_000;

const NOTICES_KEY = ['notices'] as const;

/**
 * The newest notices for the reader, and how many are unread.
 *
 * Polled once a minute while the tab is in front, and not at all while it is
 * hidden. The poll is not activity (`SESS-5`): the session guard measures
 * idleness from keystrokes, clicks, scrolls and touches only, and a query
 * refetching on a timer is none of those, so it never keeps the browser's
 * idle clock from running.
 */
export const noticesQuery = () =>
  queryOptions({
    queryKey: NOTICES_KEY,
    queryFn: ({ signal }) =>
      request(
        `/api/v1/me/notifications?limit=${String(BELL_PAGE)}&offset=0`,
        noticesSchema,
        // And the server is told so, or the once-a-minute check would keep
        // an idle session alive on its side however still the browser is.
        { signal, poll: true },
      ),
    refetchInterval: BELL_CHECK_EVERY_MS,
    refetchIntervalInBackground: false,
  });

/** A notice as it reads once opened. */
function markedRead(page: Notices, id: string | null, at: string): Notices {
  let cleared = 0;
  const items = page.items.map((notice) => {
    if (notice.read_at !== null || (id !== null && notice.id !== id)) return notice;
    cleared += 1;
    return { ...notice, read_at: at };
  });
  // Marking every one read clears the count, including notices past this page.
  const unread = id === null ? 0 : Math.max(0, page.unread_count - cleared);
  return { ...page, items, unread_count: unread };
}

/**
 * Apply a change to the cached page before the request goes, and hand back
 * what to put back if it fails.
 */
async function optimistically(
  client: QueryClient,
  id: string | null,
): Promise<{ previous: Notices | undefined }> {
  await client.cancelQueries({ queryKey: NOTICES_KEY });
  const previous = client.getQueryData<Notices>(NOTICES_KEY);
  if (previous) {
    client.setQueryData<Notices>(
      NOTICES_KEY,
      markedRead(previous, id, new Date().toISOString()),
    );
  }
  return { previous };
}

const emptyResponse = z.undefined();

/**
 * Mark one notice read.
 *
 * Optimistic: the bell's number drops the moment it is chosen, and comes back
 * if the server refuses. A 404 means it was never this reader's (`NOT-23`).
 */
export function useMarkNoticeRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      request(`/api/v1/me/notifications/${id}/read`, emptyResponse, { method: 'POST' }),
    onMutate: (id: string) => optimistically(client, id),
    onError: (_error, _id, context) => {
      if (context?.previous) client.setQueryData(NOTICES_KEY, context.previous);
    },
    onSettled: () => client.invalidateQueries({ queryKey: NOTICES_KEY }),
  });
}

export const markedAllSchema = z.object({ marked: z.number() });

/** Mark every one of the reader's notices read. */
export function useMarkAllNoticesRead() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () =>
      request('/api/v1/me/notifications/read-all', markedAllSchema, { method: 'POST' }),
    onMutate: () => optimistically(client, null),
    onError: (_error, _vars, context) => {
      if (context?.previous) client.setQueryData(NOTICES_KEY, context.previous);
    },
    onSettled: () => client.invalidateQueries({ queryKey: NOTICES_KEY }),
  });
}

// ── What each person chooses ──────────────────────────────────────────────────

export const choiceSchema = z.object({
  kind: z.enum(NOTICE_KINDS),
  label: z.string(),
  detail: z.string(),
  in_app: z.boolean(),
  email: z.boolean(),
  /** In the person's own Slack direct messages. On until they turn it off. */
  slack: z.boolean(),
});

export const choicesSchema = z.object({
  items: z.array(choiceSchema),
  /** Whether Slack is the chat tool in use, so its switches are worth showing. */
  slack_offered: z.boolean(),
});

export type NoticeChoice = z.infer<typeof choiceSchema>;
export type NoticeChoices = z.infer<typeof choicesSchema>;

/** One row's answer, as it is sent: the request body's own generated type. */
export type ChoiceChange =
  components['schemas']['src__api__v1__me__notifications__ChoiceIn'];

const CHOICES_KEY = ['notice-choices'] as const;
const SAVE_CHOICE_KEY = ['notice-choices', 'save'] as const;

/**
 * The reader's choices, one per row of §3.2, with the server's own words for
 * each. The labels are the server's so the screen and the email agree.
 */
export const noticeChoicesQuery = () =>
  queryOptions({
    queryKey: CHOICES_KEY,
    queryFn: ({ signal }) =>
      request('/api/v1/me/notification-preferences', choicesSchema, { signal }),
    // They change when this person changes them and at no other time.
    staleTime: Infinity,
  });

/**
 * Save one row.
 *
 * The switch moves before the request goes, and moves back if the server
 * refuses (`NOT-20`). Only the changed row is sent: a row left out is left as
 * it was, so two quick changes to different rows cannot undo each other.
 */
export function useSaveNoticeChoice() {
  const client = useQueryClient();
  return useMutation({
    mutationKey: SAVE_CHOICE_KEY,
    mutationFn: (change: ChoiceChange) =>
      request('/api/v1/me/notification-preferences', choicesSchema, {
        method: 'PUT',
        body: { items: [change] },
      }),
    onMutate: async (change: ChoiceChange) => {
      await client.cancelQueries({ queryKey: CHOICES_KEY });
      const previous = client.getQueryData<NoticeChoices>(CHOICES_KEY);
      if (previous) {
        client.setQueryData<NoticeChoices>(CHOICES_KEY, {
          ...previous,
          items: previous.items.map((row) =>
            row.kind === change.kind
              ? {
                  ...row,
                  in_app: change.in_app,
                  email: change.email,
                  slack: change.slack ?? row.slack,
                }
              : row,
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _change, context) => {
      if (context?.previous) client.setQueryData(CHOICES_KEY, context.previous);
    },
    onSuccess: (fresh) => {
      // What the server kept wins over what was asked for -- unless another
      // change is still on its way, whose switch this answer predates and
      // would flick back for a moment.
      if (client.isMutating({ mutationKey: SAVE_CHOICE_KEY }) <= 1) {
        client.setQueryData(CHOICES_KEY, fresh);
      }
    },
  });
}
